import React, { useEffect, useRef, type JSX } from 'react';

import ReactECharts from 'echarts-for-react';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { Box, Button, MenuItem, Radio, Stack } from '@mui/material';
import { StyledMenu } from 'pages/Chart/StyledMenu';
import { CustomTransComponent } from 'components';
import { getChartMaxValue, getIntervalValue, getStringGroupPath } from 'pages/WidgetManagement/common/utils/helpers';
import { useTransformData } from 'hooks/useTransformData';
import { useGetChannelUnitsQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { seriesColors } from '@fiji/common';

type TelemetryChartProps = {
    type?: 'bar' | 'column';
    widgetTypeId?: string;
    route?: string;
    state?: string;
    maxCount?: number;
    secondary: any[];
    widgetName?: string;
    [key: string]: any;
};

const DEFAULT_CHART_HEIGHT = 700;
const BAR_ITEM_HEIGHT = 90;
const MIN_COLUMN_CHART_HEIGHT = 60;
const VIEW_MODE = 'view';

const cloneSeriesData = (secondary: any[]): any[] => JSON.parse(JSON.stringify(secondary ?? []));

const UNIT_MATCHER_BY_SELECTED_STATE: {
    true: () => true;
    false: (item: any, selectedUnit: string) => boolean;
} = {
    true: () => true,
    false: (item: any, selectedUnit: string) => item?.unit === selectedUnit,
};

const shouldIncludeSeriesByUnit = (item: any, selectedUnit: string): boolean =>
    UNIT_MATCHER_BY_SELECTED_STATE[String(!selectedUnit) as 'true' | 'false'](item, selectedUnit);

const prepareSeriesData = (secondary: any[], selectedUnit: string, maxCount: number): any[] => {
    const clonedData = cloneSeriesData(secondary);
    const filteredData = clonedData?.filter((item: any) => shouldIncludeSeriesByUnit(item, selectedUnit));
    return filteredData.slice(0, maxCount);
};

const computeBaseChartHeight = (chartHeight: number | undefined, isBar: boolean, axisChartHeight: number): number => {
    const reservedHeight = 70 + Number(isBar) * axisChartHeight;
    const defaultBaseHeightSource = DEFAULT_CHART_HEIGHT + 70;
    return Math.floor((chartHeight ?? defaultBaseHeightSource) - reservedHeight);
};

const buildSharedValueAxis = (maxDataValue: number, interval: number): any => ({
    type: 'value',
    min: 0,
    ...(maxDataValue > 0 && { max: Math.ceil(maxDataValue * 1.2) }),
    ...(Number.isFinite(interval) && { interval }),
});

const buildCategoryAxisData = (data: any[]): string[] => data?.map((_: any, i: number) => `item_${i}`);

const buildBarAxisOption = (sharedValueAxis: any, unit: string): any => ({
    tooltip: { show: false },
    grid: {
        containLabel: false,
        left: 20,
        right: 30,
        top: 0,
        bottom: 20,
    },
    xAxis: {
        ...sharedValueAxis,
        axisLabel: {
            formatter: `{value} ${unit}`,
        },
        splitLine: { show: false },
        axisLine: {
            show: true,
            lineStyle: { color: '#e0e0e0' },
        },
    },
    yAxis: {
        type: 'category',
        data: [],
        show: false,
    },
    series: [],
});

const buildBarChartOption = (sharedValueAxis: any, data: any[], series: any[]): any => ({
    tooltip: { show: false },
    grid: {
        containLabel: false,
        left: 20,
        right: 30,
        top: 20,
        bottom: 10,
    },
    xAxis: {
        ...sharedValueAxis,
        show: false,
    },
    yAxis: {
        type: 'category',
        data: buildCategoryAxisData(data),
        axisLabel: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
    },
    series,
});

const buildColumnChartOption = ({
    data,
    unit,
    maxDataValue,
    height,
    series,
}: {
    data: any[];
    unit: string;
    maxDataValue: number;
    height: number;
    series: any[];
}): any => {
    const gridPadding = 10 + Number(height >= 150) * 10;
    const splitNumber = Math.max(2, Math.min(6, Math.floor(height / 60)));

    return {
        tooltip: { show: false },
        grid: {
            containLabel: false,
            left: 60,
            right: 20,
            top: gridPadding,
            bottom: gridPadding,
        },
        xAxis: {
            type: 'category',
            data: buildCategoryAxisData(data),
            axisLabel: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
        },
        yAxis: {
            type: 'value',
            min: 0,
            ...(maxDataValue > 0 && { max: Math.ceil(maxDataValue * 1.2) }),
            axisLabel: {
                formatter: `{value} ${unit}`,
            },
            splitNumber,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false },
        },
        series,
    };
};

const buildUnitsPayload = (secondary: any[]): any[] => {
    const channelMap = new Map<string, string[]>();

    (secondary ?? []).forEach((item: any) => {
        const existingChannels = channelMap.get(item.deviceId) ?? [];
        channelMap.set(item.deviceId, [...existingChannels, item.channelId]);
    });

    return Array.from(channelMap.entries()).map(([deviceId, channelId]) => ({ deviceId, channelId }));
};

const SERIES_DISPLAY_NAME_BY_MODE: {
    true: (item: any) => string;
    false: (item: any) => string;
} = {
    true: (item: any) => `${item?.displayName}/${getStringGroupPath(item?.devicePath, item?.deviceName)}`,
    false: (item: any) => item?.displayName ?? '',
};

const resolveSeriesDisplayName = (item: any, multiDevice: boolean): string =>
    SERIES_DISPLAY_NAME_BY_MODE[String(multiDevice) as 'true' | 'false'](item);

const normalizeSeriesValue = (value: any): number => Number(value) || 0;

const QUERY_SKIP_RESOLVER_BY_MODE: {
    view: (hasPayload: boolean) => boolean;
    default: () => boolean;
} = {
    view: (hasPayload: boolean) => !hasPayload,
    default: () => true,
};

const resolveUnitsQuerySkip = (mode: string, unitsPayloadLength: number): boolean => {
    const hasPayload = unitsPayloadLength > 0;
    const resolver = QUERY_SKIP_RESOLVER_BY_MODE[mode as typeof VIEW_MODE] ?? QUERY_SKIP_RESOLVER_BY_MODE.default;
    return resolver(hasPayload);
};

const resolveSelectedUnitSeed = (unitList: any, previous: any): any => unitList?.data?.[0] ?? previous;

const createSeriesValueLabelFormatter =
    (data: any[], pointConversion: (value: any) => any, multiDevice: boolean): ((params: any) => string) =>
    (params: any): string => {
        const item = data[params.dataIndex];
        const unit = item?.unit ?? '';
        const val = pointConversion(params.value);
        const name = resolveSeriesDisplayName(item, multiDevice);
        return `{bold|${val}}${unit} ${name}`;
    };

const buildBarSeriesData = (data: any[], formatter: (params: any) => string): any[] => {
    const barWidth = 25;
    return [
        {
            type: 'bar' as const,
            barWidth,
            barCategoryGap: '40%',
            data: data?.map((item: any, index: number) => ({
                value: normalizeSeriesValue(item?.value),
                itemStyle: {
                    color: seriesColors[index],
                    borderRadius: [0, 5, 5, 0],
                },
            })),
            emphasis: {
                focus: 'self',
            },
            label: {
                show: true,
                position: [0, barWidth + 5],
                formatter,
                rich: {
                    bold: { fontWeight: 700 },
                },
                fontSize: 14,
                fontWeight: 500,
                color: '#000',
            },
        },
    ];
};

const buildColumnSeriesData = (data: any[], formatter: (params: any) => string): any[] => {
    const barWidth = 25 - Number((data?.length ?? 0) > 8) * 5;
    return [
        {
            type: 'bar' as const,
            barWidth,
            barCategoryGap: '40%',
            data: data?.map((item: any, index: number) => ({
                value: normalizeSeriesValue(item?.value),
                itemStyle: {
                    color: seriesColors[index],
                    borderRadius: [5, 5, 0, 0],
                },
            })),
            emphasis: {
                focus: 'self',
            },
            label: {
                show: true,
                position: [barWidth + 15, '100%'] as [number, string],
                rotate: 90,
                verticalAlign: 'middle',
                align: 'left',
                formatter,
                rich: {
                    bold: { fontWeight: 700 },
                },
                fontSize: 14,
                fontWeight: 500,
                color: '#000',
            },
        },
    ];
};

const resolveBarChartState = (
    data: any[],
    unit: string,
    interval: number,
    maxDataValue: number,
    baseHeight: number,
    formatter: (params: any) => string
): {
    chartStyle: { height: number; width: string; flexShrink: number };
    axisOption: any;
    option: any;
} => {
    const minBarHeight = (data?.length ?? 0) * BAR_ITEM_HEIGHT;
    const effectiveHeight = Math.max(baseHeight, minBarHeight);
    const sharedValueAxis = buildSharedValueAxis(maxDataValue, interval);
    const series = buildBarSeriesData(data, formatter);

    return {
        chartStyle: { height: effectiveHeight, width: '100%', flexShrink: 0 },
        axisOption: buildBarAxisOption(sharedValueAxis, unit),
        option: buildBarChartOption(sharedValueAxis, data, series),
    };
};

const resolveColumnChartState = (
    data: any[],
    unit: string,
    maxDataValue: number,
    baseHeight: number,
    formatter: (params: any) => string
): {
    chartStyle: { height: number; width: string; flexShrink: number };
    axisOption: any;
    option: any;
} => {
    const effectiveBaseHeight = Math.max(baseHeight, MIN_COLUMN_CHART_HEIGHT);
    const series = buildColumnSeriesData(data, formatter);

    return {
        chartStyle: { height: effectiveBaseHeight, width: '100%', flexShrink: 0 },
        axisOption: { xAxis: {}, yAxis: {}, series: [] },
        option: buildColumnChartOption({
            data,
            unit,
            maxDataValue,
            height: effectiveBaseHeight,
            series,
        }),
    };
};

type TelemetryChartStateContext = {
    data: any[];
    unit: string;
    interval: number;
    maxDataValue: number;
    baseHeight: number;
    formatter: (params: any) => string;
};

type TelemetryChartResolvedState = {
    chartStyle: { height: number; width: string; flexShrink: number };
    axisOption: any;
    option: any;
};

const CHART_STATE_RESOLVER_BY_TYPE: {
    bar: (context: TelemetryChartStateContext) => TelemetryChartResolvedState;
    column: (context: TelemetryChartStateContext) => TelemetryChartResolvedState;
} = {
    bar: (context) =>
        resolveBarChartState(
            context.data,
            context.unit,
            context.interval,
            context.maxDataValue,
            context.baseHeight,
            context.formatter
        ),
    column: (context) =>
        resolveColumnChartState(
            context.data,
            context.unit,
            context.maxDataValue,
            context.baseHeight,
            context.formatter
        ),
};

const CHART_TYPE_BY_INPUT: { bar: 'bar' } = {
    bar: 'bar',
};

const normalizeChartType = (chartType: string | undefined): 'bar' | 'column' =>
    CHART_TYPE_BY_INPUT[chartType as 'bar'] ?? 'column';

const resolveChartUnit = (data: any[]): string => getChartMaxValue(data)?.unit ?? '';

const resolveMaxTelemetryValue = (data: any[]): number => Number(getChartMaxValue(data)?.value ?? 0);

const resolveTelemetryChartResolver = (
    chartType: string | undefined
): ((context: TelemetryChartStateContext) => TelemetryChartResolvedState) => {
    const normalizedChartType = normalizeChartType(chartType);
    return CHART_STATE_RESOLVER_BY_TYPE[normalizedChartType];
};

const buildTelemetryChartStateContext = ({
    data,
    chartType,
    chartHeight,
    axisChartHeight,
    multiDevice,
    pointConversion,
}: {
    data: any[];
    chartType: string | undefined;
    chartHeight: number | undefined;
    axisChartHeight: number;
    multiDevice: boolean;
    pointConversion: (value: any) => any;
}): {
    resolver: (context: TelemetryChartStateContext) => TelemetryChartResolvedState;
    context: TelemetryChartStateContext;
} => {
    const normalizedChartType = normalizeChartType(chartType);
    const context: TelemetryChartStateContext = {
        data,
        unit: resolveChartUnit(data),
        interval: Number(getIntervalValue(data)),
        maxDataValue: resolveMaxTelemetryValue(data),
        baseHeight: computeBaseChartHeight(chartHeight, normalizedChartType === 'bar', axisChartHeight),
        formatter: createSeriesValueLabelFormatter(data, pointConversion, multiDevice),
    };

    return {
        resolver: resolveTelemetryChartResolver(chartType),
        context,
    };
};

const resolveTelemetryChartState = ({
    data,
    chartType,
    chartHeight,
    axisChartHeight,
    multiDevice,
    pointConversion,
}: {
    data: any[];
    chartType: string | undefined;
    chartHeight: number | undefined;
    axisChartHeight: number;
    multiDevice: boolean;
    pointConversion: (value: any) => any;
}): {
    chartStyle: { height: number; width: string; flexShrink: number };
    axisOption: any;
    option: any;
} => {
    const { resolver, context } = buildTelemetryChartStateContext({
        data,
        chartType,
        chartHeight,
        axisChartHeight,
        multiDevice,
        pointConversion,
    });

    return resolver(context);
};

const useInitializeSelectedUnit = (unitList: any, setSelectedUnits: React.Dispatch<any>): void => {
    useEffect(() => {
        setSelectedUnits((previous: any) => resolveSelectedUnitSeed(unitList, previous));
    }, [unitList, setSelectedUnits]);
};

const resolveTelemetryChartData = (chartWidget: TelemetryChartProps, selectedUnits: any): any[] =>
    prepareSeriesData(chartWidget?.secondary ?? [], selectedUnits, chartWidget?.maxCount ?? 10);

const resolveTelemetryChartComputationState = ({
    chartWidget,
    selectedUnits,
    chartRect,
    axisChartHeight,
    pointConversion,
}: {
    chartWidget: TelemetryChartProps;
    selectedUnits: any;
    chartRect: any;
    axisChartHeight: number;
    pointConversion: (value: any) => any;
}): TelemetryChartResolvedState => {
    const data = resolveTelemetryChartData(chartWidget, selectedUnits);
    return resolveTelemetryChartState({
        data,
        chartType: chartWidget?.type,
        chartHeight: chartRect?.chartHeight,
        axisChartHeight,
        multiDevice: Boolean(chartWidget?.multiDevice),
        pointConversion,
    });
};

const applyTelemetryChartState = (
    state: TelemetryChartResolvedState,
    setChartStyle: React.Dispatch<React.SetStateAction<{ height: number; width: string; flexShrink: number }>>,
    setAxisOption: React.Dispatch<React.SetStateAction<any>>,
    setOption: React.Dispatch<React.SetStateAction<any>>
): void => {
    setChartStyle(state.chartStyle);
    setAxisOption(state.axisOption);
    setOption(state.option);
};

const useResolvedTelemetryChartState = ({
    chartWidget,
    selectedUnits,
    chartRect,
    axisChartHeight,
    pointConversion,
}: {
    chartWidget: TelemetryChartProps;
    selectedUnits: any;
    chartRect: any;
    axisChartHeight: number;
    pointConversion: (value: any) => any;
}): TelemetryChartResolvedState =>
    React.useMemo(
        () =>
            resolveTelemetryChartComputationState({
                chartWidget,
                selectedUnits,
                chartRect,
                axisChartHeight,
                pointConversion,
            }),
        [chartWidget, selectedUnits, chartRect, axisChartHeight, pointConversion]
    );

const useApplyTelemetryChartState = (
    state: TelemetryChartResolvedState,
    setChartStyle: React.Dispatch<React.SetStateAction<{ height: number; width: string; flexShrink: number }>>,
    setAxisOption: React.Dispatch<React.SetStateAction<any>>,
    setOption: React.Dispatch<React.SetStateAction<any>>
): void => {
    useEffect(() => {
        applyTelemetryChartState(state, setChartStyle, setAxisOption, setOption);
    }, [state, setChartStyle, setAxisOption, setOption]);
};

const useTelemetryChartOptions = ({
    chartWidget,
    selectedUnits,
    chartRect,
    axisChartHeight,
    pointConversion,
    setChartStyle,
    setAxisOption,
    setOption,
}: {
    chartWidget: TelemetryChartProps;
    selectedUnits: any;
    chartRect: any;
    axisChartHeight: number;
    pointConversion: (value: any) => any;
    setChartStyle: React.Dispatch<React.SetStateAction<{ height: number; width: string; flexShrink: number }>>;
    setAxisOption: React.Dispatch<React.SetStateAction<any>>;
    setOption: React.Dispatch<React.SetStateAction<any>>;
}): void => {
    const state = useResolvedTelemetryChartState({
        chartWidget,
        selectedUnits,
        chartRect,
        axisChartHeight,
        pointConversion,
    });
    useApplyTelemetryChartState(state, setChartStyle, setAxisOption, setOption);
};

const UNITS_MENU_END_ICON_BY_STATE: {
    true: JSX.Element;
    false: JSX.Element;
} = {
    true: <ArrowDropUp />,
    false: <ArrowDropDown />,
};

const resolveUnitsMenuEndIcon = (open: boolean): JSX.Element =>
    UNITS_MENU_END_ICON_BY_STATE[String(open) as 'true' | 'false'];

const AXIS_CHART_RENDERER_BY_STATE: {
    true: (axisOption: any, axisChartHeight: number) => JSX.Element;
    false: () => null;
} = {
    true: (axisOption: any, axisChartHeight: number) => (
        <ReactECharts option={axisOption} style={{ height: axisChartHeight, width: '100%', flexShrink: 0 }} />
    ),
    false: () => null,
};

const renderAxisChart = (isBarChart: boolean, axisOption: any, axisChartHeight: number): JSX.Element | null =>
    AXIS_CHART_RENDERER_BY_STATE[String(isBarChart) as 'true' | 'false'](axisOption, axisChartHeight);

const UNITS_BUTTON_ARIA_PROPS_BY_STATE: {
    true: { ariaControls: string; ariaExpanded: 'true' };
    false: { ariaControls: undefined; ariaExpanded: undefined };
} = {
    true: { ariaControls: 'demo-customized-menu', ariaExpanded: 'true' },
    false: { ariaControls: undefined, ariaExpanded: undefined },
};

const resolveUnitsButtonAriaProps = (
    open: boolean
): { ariaControls: string | undefined; ariaExpanded: 'true' | undefined } =>
    UNITS_BUTTON_ARIA_PROPS_BY_STATE[String(open) as 'true' | 'false'];

const renderUnitsMenuItems = (
    unitList: any,
    selectedUnits: any,
    setSelectedUnits: React.Dispatch<any>
): React.ReactNode =>
    unitList?.data?.map((item: any): any => (
        <MenuItem key={item} value={item} className="w-100">
            <Radio
                className="margin-right-16"
                checked={selectedUnits === item}
                onChange={(): void => setSelectedUnits(item)}
            />
            {item}
        </MenuItem>
    ));

const useUnitsMenuState = (): {
    anchorEl: HTMLElement | null;
    open: boolean;
    handleClick: (event: React.MouseEvent<HTMLElement>) => void;
    handleClose: () => void;
} => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (): void => {
        setAnchorEl(null);
    };

    return { anchorEl, open, handleClick, handleClose };
};

const useTelemetryUnitsData = ({
    secondary,
    mode,
    selectedUnits,
    setSelectedUnits,
}: {
    secondary: any[];
    mode: string;
    selectedUnits: any;
    setSelectedUnits: React.Dispatch<any>;
}): {
    unitList: any;
    unitMenuItems: React.ReactNode;
} => {
    const unitsPayload = React.useMemo(() => buildUnitsPayload(secondary ?? []), [secondary]);
    const shouldSkipUnitsQuery = resolveUnitsQuerySkip(mode, unitsPayload?.length ?? 0);
    const { data: unitList } = useGetChannelUnitsQuery(unitsPayload, {
        skip: shouldSkipUnitsQuery,
    });
    const unitMenuItems = renderUnitsMenuItems(unitList, selectedUnits, setSelectedUnits);

    return { unitList, unitMenuItems };
};

type TelemetryChartViewProps = {
    mode: string;
    anchorEl: HTMLElement | null;
    handleClick: (event: React.MouseEvent<HTMLElement>) => void;
    handleClose: () => void;
    unitsButtonAriaControls: string | undefined;
    unitsButtonAriaExpanded: 'true' | undefined;
    unitsMenuEndIcon: JSX.Element;
    unitMenuItems: React.ReactNode;
    option: any;
    chartRef: React.MutableRefObject<any>;
    chartStyle: { height: number; width: string; flexShrink: number };
    axisChartElement: JSX.Element | null;
};

type TelemetryChartViewModel = TelemetryChartViewProps;

const TelemetryChartView = ({
    mode,
    anchorEl,
    handleClick,
    handleClose,
    unitsButtonAriaControls,
    unitsButtonAriaExpanded,
    unitsMenuEndIcon,
    unitMenuItems,
    option,
    chartRef,
    chartStyle,
    axisChartElement,
}: TelemetryChartViewProps): JSX.Element => (
    <Stack
        sx={{
            height: `calc(100% - 97px)`,
            display: 'flex',
            flexDirection: 'column',
        }}
        className="teleChart"
    >
        <Stack
            flexShrink={0}
            display="flex"
            flexDirection="row"
            justifyContent="flex-end"
            alignItems={'flex-end'}
            p={2}
            pr={5}
        >
            <Box mr={2}>
                <Button
                    disabled={mode !== 'view'}
                    id="demo-customized-button"
                    aria-controls={unitsButtonAriaControls}
                    aria-haspopup="true"
                    aria-expanded={unitsButtonAriaExpanded}
                    sx={{
                        backgroundColor: 'transparent',
                        color: '#424E54',
                    }}
                    onClick={handleClick}
                    endIcon={unitsMenuEndIcon}
                >
                    <CustomTransComponent translationKey={'COMMON:UNITS'} />
                </Button>
                <StyledMenu
                    id="demo-customized-menu"
                    MenuListProps={{
                        'aria-labelledby': 'demo-customized-button',
                    }}
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                >
                    {unitMenuItems}
                </StyledMenu>
            </Box>
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <ReactECharts option={option} ref={chartRef} style={chartStyle} notMerge={true} />
        </Box>
        {axisChartElement}
    </Stack>
);

const useTelemetryChartViewModel = ({
    chartWidget,
    mode,
    chartRect,
    pointConversion,
}: {
    chartWidget: TelemetryChartProps;
    mode: string;
    chartRect: any;
    pointConversion: (value: any) => any;
}): TelemetryChartViewModel => {
    const AXIS_CHART_HEIGHT = 50;
    const chartRef = useRef<any>(null);
    const isBarChart = chartWidget?.type === 'bar';
    const [option, setOption] = React.useState<any>({ xAxis: {}, yAxis: {}, series: [] });
    const [axisOption, setAxisOption] = React.useState<any>({ xAxis: {}, yAxis: {}, series: [] });
    const [chartStyle, setChartStyle] = React.useState<{ height: number; width: string; flexShrink: number }>({
        height: 700,
        width: '100%',
        flexShrink: 0,
    });

    const [selectedUnits, setSelectedUnits] = React.useState<any>(chartWidget?.selectedUnit ?? '');
    const { anchorEl, open, handleClick, handleClose } = useUnitsMenuState();
    const { unitList, unitMenuItems } = useTelemetryUnitsData({
        secondary: chartWidget?.secondary ?? [],
        mode,
        selectedUnits,
        setSelectedUnits,
    });

    const unitsButtonAriaProps = resolveUnitsButtonAriaProps(open);
    const unitsMenuEndIcon = resolveUnitsMenuEndIcon(open);
    const axisChartElement = renderAxisChart(isBarChart, axisOption, AXIS_CHART_HEIGHT);

    useInitializeSelectedUnit(unitList, setSelectedUnits);
    useTelemetryChartOptions({
        chartWidget,
        selectedUnits,
        chartRect,
        axisChartHeight: AXIS_CHART_HEIGHT,
        pointConversion,
        setChartStyle,
        setAxisOption,
        setOption,
    });

    return {
        mode,
        anchorEl,
        handleClick,
        handleClose,
        unitsButtonAriaControls: unitsButtonAriaProps.ariaControls,
        unitsButtonAriaExpanded: unitsButtonAriaProps.ariaExpanded,
        unitsMenuEndIcon,
        unitMenuItems,
        option,
        chartRef,
        chartStyle,
        axisChartElement,
    };
};

const TelemetryChart = ({
    widgetData: chartWidget,
    mode,
    chartRect,
}: {
    widgetData: TelemetryChartProps;
    mode: string;
    chartRect: any;
}): JSX.Element => {
    const { pointConversion } = useTransformData();
    const viewModel = useTelemetryChartViewModel({
        chartWidget,
        mode,
        chartRect,
        pointConversion,
    });

    return <TelemetryChartView {...viewModel} />;
};
export default TelemetryChart;
